/**
 * Display helpers for patient links, shared by the Patients tab and the EHR
 * sync dialogs: the patient's name, the DOB and sex line, and which row actions
 * a link's status allows.
 */
import type { EhrPatientLinkStatus, EhrPatientSummary } from '@models/ehr-sync';

/** Statuses a link may be paused from. MUST match the backend's PAUSABLE_STATUSES. */
export const PAUSABLE_LINK_STATUSES: ReadonlySet<EhrPatientLinkStatus> =
  new Set(['pending', 'autoLinked', 'linked']);

/** Filter order on the Patients tab, as in the design. */
export const LINK_STATUS_ORDER: EhrPatientLinkStatus[] = [
  'pending',
  'needsReview',
  'autoLinked',
  'linked',
  'rejected',
  'noMatch',
  'paused',
  'unlinked',
];

/**
 * "First Last", or null when the patient is gone or has no name.
 * @param patient The patient summary, if any
 * @returns The display name
 */
export function patientName(
  patient: EhrPatientSummary | null | undefined
): string | null {
  const name = [patient?.firstName, patient?.lastName]
    .filter(Boolean)
    .join(' ');
  return name || null;
}

const SEX_ABBREVIATION: Record<string, string> = {
  female: 'F',
  male: 'M',
};

/**
 * The FHIR gender as the one-letter abbreviation the design uses, or null for
 * other and unknown values.
 * @param gender A FHIR AdministrativeGender code
 * @returns 'F', 'M' or null
 */
export function sexAbbreviation(gender: string | null | undefined) {
  return gender ? (SEX_ABBREVIATION[gender] ?? null) : null;
}
